import { Trans, useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import { Text } from 'designSystem/components/Text'
import { RevisionCompareContent } from 'features/knowledgeDocumentViewer/components/revisionCompare/RevisionCompareContent'
import { EMPTY_CANONICAL_REVISION_MODEL } from 'features/knowledgeDocumentViewer/components/revisionCompare/revisionMemberCanonical'
import { useRevisionDiffDetailsForDocument } from 'features/knowledgeDocumentViewer/hooks/useRevisionDiffDetails'
import { X } from 'lucide-react'
import React, { useMemo } from 'react'
import styled from 'styled-components'

import type { RevisionComparePreview } from './previewDocumentHandler'
import { resolveRevisionPairDetail } from './resolveRevisionPairDetail'

const PanelRoot = styled.div`
  display: flex;
  height: 100%;
  min-height: 0;
  flex-direction: column;
`

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-bottom: 0.0625rem solid var(--border);
`

const PanelBody = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
  padding: 0.75rem;
`

const PanelStatus = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  color: var(--muted-foreground);
  font-size: 0.8125rem;
  text-align: center;
`

export function RevisionComparePreviewPanel({
  revision,
  siteId,
  onClose,
}: {
  revision: RevisionComparePreview
  siteId: string
  onClose: () => void
}): React.ReactElement {
  const { t } = useLingui()
  const documentIds = useMemo(
    () => [revision.fromDocumentId, revision.toDocumentId],
    [revision.fromDocumentId, revision.toDocumentId]
  )
  const { details, isLoading } = useRevisionDiffDetailsForDocument(
    documentIds,
    siteId
  )
  const pairDetail = useMemo(
    () => resolveRevisionPairDetail(details, revision),
    [details, revision]
  )

  return (
    <PanelRoot>
      <PanelHeader>
        <Text variant='body' weight='medium' truncate>
          {t`What changed`}
        </Text>
        <Button
          type='button'
          variant='ghost'
          size='xs'
          aria-label={t`Close revision compare`}
          onClick={onClose}>
          <X />
        </Button>
      </PanelHeader>
      <PanelBody>
        {pairDetail ? (
          <RevisionCompareContent
            open
            details={details}
            canonicalRevisionModel={EMPTY_CANONICAL_REVISION_MODEL}
            currentBqDocumentId={revision.toDocumentId}
            currentPageNum={pairDetail.data.to_page_num}
            initialDetailId={pairDetail.entryId}
          />
        ) : (
          <PanelStatus>
            {isLoading ? (
              <Trans>Loading comparison…</Trans>
            ) : (
              <Trans>
                This comparison is unavailable for the requested revisions.
              </Trans>
            )}
          </PanelStatus>
        )}
      </PanelBody>
    </PanelRoot>
  )
}
